/* eslint-disable */
// AttachmentCard — an attachment, drawn as ONE object.
//
// Two presentations, and the difference is whether the row is a destination:
//
//   interactive — the notification surfaces (corner card, drawer row). The
//                 WHOLE row is a <button>: thumbnail, code, family, name and a
//                 chevron. Pressing it opens the record.
//   display     — the composer's picker, where the row is NOT a destination:
//                 clicking a result attaches it, and an attached row is just a
//                 chip. A nested <button> there would be invalid HTML and would
//                 navigate away mid-compose. §7.2 keeps the composer unchanged,
//                 so it keeps this presentation.
//
// ⚠️ WHY THE FULL-WIDTH "Open in <portal>" BUTTON IS GONE. It appeared only on
// families that had a route, so two attachments in one card looked like two
// different kinds of thing — which is what David saw. Every family has a route
// now (entityRoutes' ATTACH_ROUTES), so the row itself is the target and the
// affordance is uniform.
//
// ⚠️ NOTHING RENDERED HERE COMES FROM THE PAYLOAD. The payload gave a `kind`
// and an `id`; code, name, detail and picture all come from the row the reader
// fetched under their own RLS. That rule is P6's and it still stands.
//
// ⚠️ "NOT AVAILABLE" IS NOT AN ERROR, AND IT IS NOT A BUTTON. Same geometry so
// the list does not jump, dashed border, a lock where the picture would be, and
// deliberately NOT focusable — a control that cannot act must not be reachable
// as though it could. The family label is omitted rather than clipped: the
// shipped build truncated it to "Develop…", which reads as damage.

import React, { useState } from 'react';
import { familyFor } from '../../utils/attachmentRegistry';
import { openAttachment, canOpenAttachment } from '../../utils/entityRoutes';
import styles from './attachments.module.css';

const Chevron = () => (
  <svg className={styles.chev} viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const Lock = () => (
  <svg className={styles.lockIco} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

function Pill({ fam }) {
  return (
    <span
      className={styles.pill}
      style={{ color: fam.pill.fg, background: fam.pill.bg, borderColor: fam.pill.bd }}
    >
      {fam.label}
    </span>
  );
}

// The tile a family without a picture gets, and the tile a broken image falls
// back to: the code, centred, in the family's own colour. A real design, not an
// apology — never a broken-image icon.
function CodeTile({ fam, code }) {
  return (
    <span
      className={styles.tile}
      style={{ color: fam.pill.fg, background: fam.pill.bg, borderColor: fam.pill.bd }}
      aria-hidden="true"
    >
      <span className={styles.tileCode}>{code || fam.label}</span>
    </span>
  );
}

export function AttachmentCard({ entity, refKind, interactive = false, onNavigate }) {
  const [broke, setBroke] = useState(false);
  const fam = familyFor(entity ? entity.kind : refKind);
  if (!fam) return null;                       // unknown kind — skipped, never guessed

  // ---- resolved to nothing: RLS hid it, or it is gone ----------------------
  if (entity === null) {
    return (
      <div className={styles.row + ' ' + styles.rowGone} data-test="attachment-unavailable">
        <span className={styles.goneTile} aria-hidden="true"><Lock /></span>
        <span className={styles.gone}>
          This {fam.label.toLowerCase()} is no longer available to you.
        </span>
      </div>
    );
  }

  // ---- still resolving: same geometry, so nothing jumps -------------------
  if (!entity) {
    return (
      <div className={styles.row + ' ' + styles.rowSkel} data-test="attachment-loading">
        <span className={styles.skelTile} />
        <span className={styles.skelText}><i /><i /></span>
      </div>
    );
  }

  const media = entity.image && !broke ? (
    <img
      className={styles.img}
      src={entity.image}
      alt=""
      loading="lazy"
      onError={() => setBroke(true)}
    />
  ) : (
    <CodeTile fam={fam} code={entity.code} />
  );

  const middle = (
    <span className={styles.mid}>
      <span className={styles.codeRow}>
        <span className={styles.code}>{entity.code}</span>
        <Pill fam={fam} />
      </span>
      {entity.name && <span className={styles.name} dir="auto">{entity.name}</span>}
    </span>
  );

  // Every family has a route today, so `reachable` is true in practice — but a
  // row that cannot act must never be a button that does nothing.
  const reachable = interactive && canOpenAttachment(entity);

  if (!reachable) {
    return (
      <div className={styles.row + ' ' + styles.rowFlat} data-test={'attachment-' + entity.code}>
        {media}
        {middle}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={styles.row + ' ' + styles.rowBtn}
      // The notification row underneath has its own click — it marks read. An
      // attachment press must reach the attachment and stop there.
      onClick={(e) => {
        e.stopPropagation();
        if (openAttachment(entity)) onNavigate?.();
      }}
      aria-label={'Open ' + entity.code + ', ' + fam.label}
      data-test={'attachment-' + entity.code}
    >
      {media}
      {middle}
      <Chevron />
    </button>
  );
}

export default AttachmentCard;
